import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import CheckoutForm from './CheckoutForm';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

const API_URL = import.meta.env.VITE_API_URL;
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY, {
    locale: 'en',
    appearance: {
        theme: 'stripe',
        variables: {
            fontFamily: 'system-ui, sans-serif',
            borderRadius: '6px',
        },
    },
});

const PaymentModal = ({ isOpen, onClose, packageData }) => {
    const [clientSecret, setClientSecret] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const createPaymentIntent = async () => {
            if (isOpen && packageData && user) {
                setIsLoading(true);
                setError('');

                try {
                    const { data: { session } } = await supabase.auth.getSession();

                    if (!session?.access_token) {
                        throw new Error('No authentication token available');
                    }

                    const response = await fetch(`${API_URL}/create-payment-intent`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${session.access_token}`
                        },
                        body: JSON.stringify({ packageData }),
                    });

                    const data = await response.json();

                    if (!response.ok) {
                        throw new Error(data.error || 'Failed to create payment intent');
                    }

                    if (data.error) {
                        setError(data.error);
                    } else {
                        setClientSecret(data.clientSecret);
                    }
                } catch (err) {
                    console.error('Payment Intent Error:', err);
                    setError('An error occurred while processing your request. Please try again.');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        createPaymentIntent();
    }, [isOpen, packageData, user]);

    const handleClose = () => {
        setClientSecret('');
        setError('');
        setIsLoading(false);
        onClose();
    };

    const handlePaymentSuccess = () => {
        handleClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md max-h-[calc(100vh-4rem)] overflow-y-auto my-auto">
                <div className="space-y-6">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            Purchase Credits
                            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        </DialogTitle>
                        <DialogDescription>
                            Complete your purchase of {packageData?.credits?.toLocaleString()} credits
                        </DialogDescription>
                    </DialogHeader>

                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {clientSecret && (
                        <Elements
                            stripe={stripePromise}
                            options={{
                                clientSecret,
                                appearance: {
                                    theme: 'stripe',
                                    variables: {
                                        fontFamily: 'system-ui, sans-serif',
                                        borderRadius: '6px',
                                    },
                                },
                                loader: 'auto'
                            }}
                        >
                            <div className="min-h-[300px]">
                                <CheckoutForm
                                    packageData={packageData}
                                    onSuccess={handlePaymentSuccess}
                                />
                            </div>
                        </Elements>
                    )}

                    {isLoading && !clientSecret && !error && (
                        <div className="flex justify-center items-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PaymentModal;